import _ from 'lodash'
import {useContext} from 'react'
import {Data, Layout} from 'plotly.js-basic-dist-min'
import {IEvent, IEventIdentity} from '@/features/labeling'
import {IDataSourceItem, IDataSourceItemMap, LegacyCatalogContext} from '@/features/data-sources'

export interface IThumbnailImage {
  frame_index: number
  small_image_path: string
  large_image_path: string
}

export type IThumbnailsEvent = Partial<IImageDescription> & IEventIdentity

export interface IThumbnailScene {
  timestamp: number
  frame_index: number
  score: number
  // criteria: string,
  // rank: number,
  character: number[]
}

export interface IThumbnails {
  image: IThumbnailImage[]
  scene: IThumbnailScene[]
}

export interface IImageDescription {
  url: string
  score?: number
  largeUrl?: string
  scenes?: IThumbnailScene[]
}

export interface IImageDescription {
  url: string
  largeUrl?: string
  scenes?: IThumbnailScene[]
}

export interface ICategorical {
  [key: string]: number
}

export interface IPlotData {
  data: Partial<Data>[]
  layout?: Partial<Layout>
}

/** @deprecated */
export interface IParsedMediaItem {
  _id: string
  type?: string
  catalog?: string
  vionlabs_id?: string
  ident?: string
  title?: string
  year?: number
  poster?: string
  genres?: ICategorical
  keywords?: ICategorical
  moods?: ICategorical
  moodTags?: ICategorical
  tag?: string[]
  backdrop?: IImageDescription
  backdrops?: IImageDescription[]
  embedding?: number[]
  coords?: [number, number]
  score?: number
  imdbId?: string
  imdbScore?: number
  predictions?: IEvent[]
  fps?: number
  duration?: number
  plots?: {
    [name: string]: IPlotData
  }
  [key: string]: unknown
}

/** @deprecated */
// export interface IMediaItem
// {
//     _id: string,
//     type?: string,
//     hidden?: boolean,
//     title?: string,
//     year?: number,
//     // backdrops?: IImageDescription[],
//     _append?: IMediaItem[],
//     _file?: {
//         name: string,
//         line: number,
//     },
//     [key:string]: any,
// }

/** @deprecated */
// export interface IMediaItemMap
// {
//     [id:string]: IMediaItem,
// }

// getters
export const requireOneOfMediaFields = ['title', 'poster', 'backdrop', 'embedding']

export const identFields = ['ident']

export const assetIDFields = [
  'vionlabs_id',
  'ident',
  '_id',
  'imdb_id',
  'vlid', // TODO: deprecated
  // TODO: more ID types?
]

export const titleFields = ['title']

export const typeFields = ['type']

export const yearFields = ['year', 'release_year']

export const posterFields = ['poster']

export const backdropFields = ['backdrops', 'thumbnail']

export const embeddingFields = ['embedding', 'embeddings', 'fingerprint']

export const genreFields = ['genres', 'genre', 'vionlabs_genres']

export const keywordFields = ['keywords', 'keyword']

export const moodFields = ['moods', 'mood']

export const moodTagFields = ['moodTags', 'mood_tags', 'mood_tag', 'moodtag']

export const tagFields = ['tag']

export const durationFields = ['duration']

export const imdbIdFields = ['imdb_id']

export const imdbScoreFields = ['score_imdb']

export const videoURIFields = ['uri', 'asset_name']

export const coordsFields = ['coords']

export const scoreFields = ['_score', 'score']

export const categoricalFields = [
  ...genreFields,
  ...keywordFields,
  ...moodFields,
  ...moodTagFields,
  ...tagFields,
]

export const exportableMediaFields: string[] = _.without(
  [...assetIDFields, ...titleFields, ...backdropFields, ...embeddingFields, ...categoricalFields],
  '_id'
)

/** @deprecated */
export function allOfIn(data: Partial<IDataSourceItem>, fields: string[]): any[] {
  const res = _.chain(data).pick(fields).values().compact().value()
  return res
  // const append = (
  //     _.chain(data)
  //     .get('_append')
  //     .map(v => (
  //         _.chain(v)
  //         .pick(fields)
  //         .values()
  //         .compact()
  //         .value()
  //     ))
  //     .flatten()
  //     .value()
  // )
  // return _.uniq([
  //     ...res,
  //     ...append,
  // ]);
}

export function firstOfIn(data: Partial<IDataSourceItem>, fields: string[]): any {
  return _.first(allOfIn(data, fields))
}

export function lastOfIn(data: Partial<IDataSourceItem>, fields: string[]): any {
  return _.last(allOfIn(data, fields))
}

export function getIdent(item: Partial<IDataSourceItem>): string | undefined {
  return firstOfIn(item, identFields)
}

export function getAssetID(item: Partial<IDataSourceItem>): string | undefined {
  return firstOfIn(item, assetIDFields)
}

export function getAllAssetIDs(item: Partial<IDataSourceItem>): string[] {
  return allOfIn(item, assetIDFields)
}

export function getTitle(item: Partial<IDataSourceItem>): string {
  return firstOfIn(item, titleFields)
}

export function getType(item: Partial<IDataSourceItem>): string {
  return firstOfIn(item, typeFields)
}

export function getYear(item: Partial<IDataSourceItem>): string {
  return firstOfIn(item, yearFields)
}

export function buildPosterUrl(catalogName: string, vionlabsId: string): string | undefined {
  if (!catalogName) {
    console.error('missing field', catalogName, vionlabsId)
    return
  }
  return `/api/gfp-result/posters/${vionlabsId}?catalog=${catalogName}`
}

export function buildThumbnailApiUrl(catalogName: string, path: string): string | undefined {
  if (!path) return
  return _.chain([`/api/thumbnails/catalogs/${catalogName}/thumbnails/`, path])
    .map((v) => _.trimEnd(v, '/'))
    .join('/')
    .value()
}

export function getPoster(
  item: Partial<IDataSourceItem>,
  catalogName?: string
): string | undefined {
  const res = firstOfIn(item, posterFields)
  if (!res && catalogName && item['vionlabs_id']) {
    return buildPosterUrl(catalogName, item.vionlabs_id)
  }
  return res
}

export function getBackdrop(
  item: Partial<IDataSourceItem>,
  catalogName?: string
): IImageDescription | undefined {
  if (!catalogName) return
  const res = getBackdrops(item, catalogName)
  if (!res) return undefined
  return _.chain(res).first().value()
}

export function getBackdrops(
  item: Partial<IDataSourceItem>,
  catalogName?: string
): IImageDescription[] | undefined {
  const backdrops = firstOfIn(item, backdropFields)
  if (_.intersection(_.keys(backdrops), ['image', 'scene']).length > 0) {
    const _catalog = catalogName || _.get(item, 'catalog')
    if (!_catalog) {
      console.error('No catalog name')
      return
    }
    const {image, scene} = backdrops as IThumbnails
    const sceneMap = _.chain(scene)
      .groupBy((s) => s.frame_index)
      .value()
    const images = _.chain(image)
      .map((img) => ({
        ...img,
        scenes: _.get(sceneMap, img.frame_index, []),
      }))
      // .orderBy(img => {
      //     const s = img.scenes[0] || {};
      //     const {
      //         // character,
      //         score,
      //     } = s;
      //     return [
      //         // (character || []).length,
      //         score || 0,
      //     ]
      // }, [
      //     // 'desc',
      //     'desc',
      // ])
      .value()
    return _.map(
      images,
      (img) =>
        ({
          ...img,
          url: buildThumbnailApiUrl(_catalog, img.small_image_path),
          largeUrl: buildThumbnailApiUrl(_catalog, img.large_image_path),
          scenes: img.scenes,
        }) as IImageDescription
    )
  }
  return backdrops
}

export function getEmbedding(item: Partial<IDataSourceItem>): number[] | undefined {
  return firstOfIn(item, embeddingFields) || undefined
}

export function getGenres(item: Partial<IDataSourceItem>): ICategorical {
  return _.chain(getCategoricals(item, genreFields)).first().value()
}

export function getKeywords(item: Partial<IDataSourceItem>): ICategorical {
  return _.chain(getCategoricals(item, keywordFields)).first().value()
}

export function getMoods(item: Partial<IDataSourceItem>): ICategorical {
  return _.chain(getCategoricals(item, moodFields)).first().value()
}

export function getMoodTags(item: Partial<IDataSourceItem>): ICategorical {
  return _.chain(getCategoricals(item, moodTagFields)).first().value()
}

export function getDuration(item: Partial<IDataSourceItem>): number | undefined {
  return firstOfIn(item, durationFields)
}

export function getImdbId(item: Partial<IDataSourceItem>): number | undefined {
  return firstOfIn(item, imdbIdFields)
}

export function getImdbScore(item: Partial<IDataSourceItem>): number | undefined {
  return firstOfIn(item, imdbScoreFields)
}

export function getVideoURI(item: Partial<IDataSourceItem>): string | undefined {
  return firstOfIn(item, videoURIFields)
}

/** @deprecated */
export function getAllCategoricals(
  item: Partial<IDataSourceItem>,
  fields?: string[]
): [string, ICategorical][] {
  function p([k, v]: [string, ICategorical]): [string, ICategorical] | undefined {
    if (!_.isPlainObject(v)) return
    if (_.keys(v).length === 0) return
    return [
      k,
      _.chain(v)
        .toPairs()
        .sortBy(([_k, v]) => v)
        .reverse()
        .fromPairs()
        .value(),
    ]
  }
  return _.chain(item)
    .pick(...(fields || categoricalFields))
    .toPairs()
    .map((v) => p(v))
    .compact()
    .sortBy(([k, _v]) => _.indexOf(categoricalFields, k))
    .value()
}

export function getCategoricals(item: Partial<IDataSourceItem>, fields?: string[]): ICategorical[] {
  return _.chain(getAllCategoricals(item, fields))
    .map(([_k, v]) => v)
    .value()
}

export function getCoords(item: Partial<IDataSourceItem>): [number, number] | undefined {
  try {
    const coords = firstOfIn(item, coordsFields) || []
    if (coords.length !== 2) return
    return _.map(coords, _.toNumber) as [number, number]
  } catch (error) {
    console.error('Invalid coords', item, error)
  }
}

export function getScore(item: Partial<IDataSourceItem>): number | undefined {
  return firstOfIn(item, scoreFields)
}

export function thumbnailAsEvent(data: IImageDescription): IThumbnailsEvent {
  return {
    ...data,
    type: 'thumbnail',
    start: _.get(data, 'scenes[0]["timestamp"]', -1),
  }
}

// utils
export function trySet<K extends keyof IParsedMediaItem>(
  item: IParsedMediaItem,
  key: K,
  f: (item: IParsedMediaItem, ...p: any) => any,
  ...p: any
): IParsedMediaItem {
  const val = f(item, ...(p || []))
  if (val) item[key] = val
  return item
}

export function trySetParsed(
  item: IParsedMediaItem,
  key: keyof IParsedMediaItem,
  f: (item: IParsedMediaItem, ...p: any) => any,
  p?: any
): IParsedMediaItem {
  return trySet<keyof IParsedMediaItem>(item, key, f, p)
}

export function pruneFields(item: IParsedMediaItem, keys: string[]): IParsedMediaItem {
  const foundExtraKeys = _.chain(keys)
    .filter((k) => _.get(item, k) !== undefined)
    .slice(1)
    .value()
  if (foundExtraKeys.length === 0) return item
  const res = {...item}
  _.forEach(foundExtraKeys, (k) => {
    delete res[k]
  })
  return res
}

export function parseItem(item: any, i?: number, idPrefix?: string): IDataSourceItem {
  const assetID = getAssetID(item)
  if (!assetID && i === undefined) throw new Error('Missing ID')
  const res = {
    ...item,
    _id: assetID ? assetID : _.chain([idPrefix, i]).join('_').value(),
  }
  return res
}

export function listToMap(items: any[], idPrefix?: string): IDataSourceItemMap {
  return _.chain(items)
    .map((d, i) => {
      const item = parseItem(d, i, idPrefix)
      // require at least one of fields
      // if (
      //     _.chain(_.keys(useParsedFields(item)))
      //     .intersection(requireOneOfMediaFields)
      //     .value()
      //     .length === 0
      // ) return;
      return [item._id, item]
    })
    .compact()
    .fromPairs()
    .value()
}

/** @deprecated */
export function parseMediaFields(
  item: IDataSourceItem,
  catalogName?: string
): IParsedMediaItem | undefined {
  if (!item) return
  let res: IParsedMediaItem = {...item}
  const _catalog = item['catalog'] || catalogName

  res = trySetParsed(res, '_id', getAssetID)
  res = trySetParsed(res, 'title', getTitle)
  res = trySetParsed(res, 'year', getYear)
  res = trySetParsed(res, 'poster', getPoster, _catalog)
  if (_catalog) {
    res = trySetParsed(res, 'backdrop', getBackdrop, _catalog)
    res = trySetParsed(res, 'backdrops', getBackdrops, _catalog)
  }
  res = trySetParsed(res, 'embedding', getEmbedding)
  res = trySetParsed(res, 'genres', getGenres)
  res = trySetParsed(res, 'keywords', getKeywords)
  res = trySetParsed(res, 'moods', getMoods)
  res = trySetParsed(res, 'moodTags', getMoodTags)
  res = trySetParsed(res, 'coords', getCoords)
  res = trySetParsed(res, 'score', getScore)
  res = trySetParsed(res, 'imdbId', getImdbId)
  res = trySetParsed(res, 'imdbScore', getImdbScore)

  res = pruneFields(res, embeddingFields)
  res = pruneFields(res, genreFields)
  res = pruneFields(res, keywordFields)
  res = pruneFields(res, moodFields)
  res = pruneFields(res, moodTagFields)

  return res as IParsedMediaItem
}

/** @deprecated */
export function useParsedFields(
  item?: IDataSourceItem,
  catalogName?: string
): IParsedMediaItem | undefined {
  const {apiCatalogName} = useContext(LegacyCatalogContext)
  if (!item) return
  const _catalog = item['catalog'] || catalogName || apiCatalogName
  if (!_catalog) throw new Error('No catalog name')
  const parsed = parseMediaFields(item, _catalog)
  return parsed
}

// export function createNavStateFromAnchor(item:IMediaItem)
// {
//     // TODO:
// }

/** @deprecated */
export type CategoricalKeys = 'keyword' | 'mood' | 'genre' | 'mood_tag'

/** @deprecated */
export function createNavStateFromCategorical(key: CategoricalKeys, value: string) {
  // TODO: refactor
  const sortOrders = {
    keyword: {
      field: 'custom',
      order: 'descending',
      expression: `${key}["${value}"]`,
    },
    mood: {
      field: 'custom',
      order: 'descending',
      expression: `${key}["${value}"]`,
    },
    genre: {
      field: 'custom',
      order: 'descending',
      expression: `${key}["${value}"]`,
    },
    mood_tag: {
      field: 'custom',
      order: 'descending',
      expression: `${key}["${value}"]`,
    },
  }
  const res = {
    // categoryFilters: [
    //     {
    //         type: 'include',
    //         [key]: [value],
    //     },
    // ],
    sort: {
      field: 'none',
    },
  }
  if (_.has(sortOrders, key)) res.sort = _.get(sortOrders, key)
  return res
}
